// src/email/components/ThreadView.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  downloadOrViewAttachment,
  useMarkRead,
  useMarkSpam,
  useStar,
  useThread,
  useTrash,
  useTrashThread,
  useUntrashThread,
  useDeleteThreadForever,
  useUnstar,
  useMarkThreadUnread,
} from '../api/hooks';
import { EmailBodyFrame } from './EmailBodyFrame';

const AVATAR_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#10b981',
  '#f59e0b',
  '#6366f1',
  '#06b6d4',
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function parseSender(raw) {
  if (!raw) return { name: 'Unknown', email: '' };
  // E.g. "Jia from Unstop" <noreply@unstop.news>
  const bracketMatch = raw.match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (bracketMatch) {
    let name = bracketMatch[1]?.trim().replace(/^["']|["']$/g, '');
    const email = bracketMatch[2]?.trim();
    if (!name) name = email.split('@')[0];
    return { name, email };
  }
  if (raw.includes('@')) {
    return { name: raw.split('@')[0], email: raw.trim() };
  }
  return { name: raw.trim(), email: '' };
}

function isDraftMessage(m) {
  return Boolean(m?.labelIds?.includes('DRAFT'));
}

// Separates the mail chain from the unsent draft(s) written in it
function splitDrafts(messages) {
  return {
    chain: messages.filter((m) => !isDraftMessage(m)),
    drafts: messages.filter(isDraftMessage),
  };
}

function draftPreview(m) {
  const text =
    m.snippet ||
    m.bodyText ||
    new DOMParser().parseFromString(m.bodyHtml || '', 'text/html').body.textContent ||
    '';
  return text.replace(/\s+/g, ' ').trim().slice(0, 160);
}

function formatMessageDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  
  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateStr = d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
  
  if (diffMins < 60) {
    return `${timeStr} (${diffMins <= 1 ? 'just now' : `${diffMins} min ago`})`;
  }
  if (diffHours < 24 && d.getDate() === now.getDate()) {
    return `${timeStr} (${diffHours} hr${diffHours > 1 ? 's' : ''} ago)`;
  }
  return `${dateStr}, ${timeStr}`;
}

export function ThreadView({
  threadId,
  onCompose,
  onBack,
  activeFolderName = 'Inbox',
  currentIndex,
  totalThreads = 0,
  onPrevThread,
  onNextThread,
}) {
  const isTrashFolder = activeFolderName?.toLowerCase() === 'trash';
  const isDraftsFolder = activeFolderName?.toLowerCase() === 'drafts';

  // From Drafts the thread is narrowed to the draft(s) and the chain they reply to
  const { data: thread, isLoading } = useThread(threadId, isDraftsFolder ? 'drafts' : undefined);
  const markRead = useMarkRead();
  const star = useStar();
  const unstar = useUnstar();
  const trash = useTrash();
  const trashThread = useTrashThread();
  const untrashThread = useUntrashThread();
  const deleteThreadForever = useDeleteThreadForever();
  const spam = useMarkSpam();
  const markThreadUnread = useMarkThreadUnread();

  const [viewActionLoading, setViewActionLoading] = useState(null); // 'restore' | 'trash' | 'unread' | 'spam' | null
  const [starringMessageId, setStarringMessageId] = useState(null);

  const scrollContainerRef = useRef(null);

  const handleRestoreThread = async () => {
    setViewActionLoading('restore');
    try {
      await untrashThread.mutateAsync(threadId);
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to restore thread:', err);
      setViewActionLoading(null);
    }
  };

  const handleTrashThread = async () => {
    setViewActionLoading('trash');
    try {
      await trashThread.mutateAsync(threadId);
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to trash thread:', err);
      setViewActionLoading(null);
    }
  };

  const handleMarkThreadUnread = async () => {
    setViewActionLoading('unread');
    try {
      await markThreadUnread.mutateAsync(threadId);
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to mark thread unread:', err);
      setViewActionLoading(null);
    }
  };

  const handleSpam = async (msgId) => {
    setViewActionLoading('spam');
    try {
      await spam.mutateAsync(msgId);
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to report spam:', err);
      setViewActionLoading(null);
    }
  };

  const handleToggleStar = async (msg) => {
    setStarringMessageId(msg.id);
    try {
      if (msg.isStarred) {
        await unstar.mutateAsync(msg.id);
      } else {
        await star.mutateAsync(msg.id);
      }
    } catch (err) {
      console.error('Failed to toggle star:', err);
    } finally {
      setStarringMessageId(null);
    }
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [threadId]);

  useEffect(() => {
    if (!thread?.messages) return;
    for (const m of thread.messages) {
      if (m.isUnread) markRead.mutate(m.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id]);

  // Opening a conversation from Drafts puts its draft straight into the composer
  useEffect(() => {
    if (!isDraftsFolder || !thread?.messages) return;
    const { drafts } = splitDrafts(thread.messages);
    const draft = drafts[drafts.length - 1];
    if (draft) onCompose('draft', draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id, isDraftsFolder]);

  if (!threadId) {
    return (
      <div className="thread-view-empty">
        <div className="empty-illustration">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>
        <h3>No conversation selected</h3>
        <p>Choose an email from the list to read its contents.</p>
        {onBack && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
            style={{ marginTop: '14px' }}
          >
            Show Email List
          </button>
        )}
      </div>
    );
  }

  if (isLoading || !thread) {
    return (
      <div className="thread-view-empty">
        {onBack && (
          <button
            type="button"
            className="btn-back-to-list"
            onClick={onBack}
            style={{ marginBottom: '14px' }}
            title={`Back to ${activeFolderName}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span>Back to {activeFolderName}</span>
          </button>
        )}
        <div className="loading-spinner"></div>
        <span>Loading conversation…</span>
      </div>
    );
  }

  const { chain: messages, drafts } = splitDrafts(thread.messages || []);
  const lastChainMessage = messages[messages.length - 1] || null;
  const lastMessage = lastChainMessage || drafts[drafts.length - 1];

  return (
    <div className="thread-view">
      {/* Top Gmail-style reading pane toolbar */}
      <div className="thread-view-topbar">
        <div className="thread-topbar-left">
          {onBack && (
            <button
              type="button"
              className="btn-back-to-list"
              onClick={onBack}
              title={`Back to ${activeFolderName}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>Back to {activeFolderName.charAt(0).toUpperCase() + activeFolderName.slice(1)}</span>
            </button>
          )}

          <div className="thread-topbar-actions">
            {isTrashFolder ? (
              <button
                type="button"
                className="btn-restore-thread"
                title="Restore to Inbox"
                disabled={viewActionLoading === 'restore'}
                onClick={handleRestoreThread}
              >
                {viewActionLoading === 'restore' ? (
                  <>
                    <span className="mini-spinner-green" />
                    <span>Restoring to Inbox…</span>
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 14 4 9 9 4" />
                      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                    </svg>
                    <span>Restore to Inbox</span>
                  </>
                )}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="gmail-icon-btn danger"
                  title="Move to Trash"
                  disabled={viewActionLoading === 'trash'}
                  onClick={handleTrashThread}
                >
                  {viewActionLoading === 'trash' ? (
                    <span className="mini-spinner-danger" />
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className="gmail-icon-btn"
                  title="Mark as Unread"
                  disabled={viewActionLoading === 'unread'}
                  onClick={handleMarkThreadUnread}
                >
                  {viewActionLoading === 'unread' ? (
                    <span className="mini-spinner" />
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.75 9v.906a2.25 2.25 0 0 1-1.183 1.981l-6.478 3.488M2.25 9v8.25A2.25 2.25 0 0 0 4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V13.5" />
                      <path d="M21.75 6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                    </svg>
                  )}
                </button>
                {lastMessage && (
                  <button
                    type="button"
                    className="gmail-icon-btn warn"
                    title="Report Spam"
                    disabled={viewActionLoading === 'spam'}
                    onClick={() => handleSpam(lastMessage.id)}
                  >
                    {viewActionLoading === 'spam' ? (
                      <span className="mini-spinner-warn" />
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {totalThreads > 1 && currentIndex !== undefined && (
          <div className="thread-topbar-right">
            <span className="thread-nav-counter">
              {currentIndex + 1} of {totalThreads}
            </span>
            <button
              type="button"
              className="gmail-icon-btn"
              disabled={!onPrevThread}
              onClick={onPrevThread}
              title="Previous conversation"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="gmail-icon-btn"
              disabled={!onNextThread}
              onClick={onNextThread}
              title="Next conversation"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="thread-messages-scroll" ref={scrollContainerRef}>
        <div className="thread-subject-header">
          <h2>{thread.subject || '(no subject)'}</h2>
          {messages.length > 1 && (
            <span className="thread-message-count-badge">
              {messages.length} messages
            </span>
          )}
        </div>

        {/* Unsent drafts are edited and sent from the composer, not read as a mail */}
        {[...drafts].reverse().map((d) => (
          <article key={d.id} className="message-card draft-card">
            <div className="draft-card-info">
              <span className="draft-card-label">Draft</span>
              <span className="draft-card-to">
                {d.toAddresses?.length > 0 ? `to ${d.toAddresses.join(', ')}` : 'No recipients yet'}
              </span>
              <span className="draft-card-preview">{draftPreview(d)}</span>
            </div>
            <button
              type="button"
              className="btn btn-subtle"
              onClick={() => onCompose('draft', d)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              <span>Edit &amp; Send</span>
            </button>
          </article>
        ))}

        {/* Newest message first */}
        {[...messages].reverse().map((m) => {
          const sender = parseSender(m.fromAddress);
          const initial = (sender.name || sender.email || '?').charAt(0).toUpperCase();
          const avatarBg = getAvatarColor(sender.name || sender.email);
          const formattedDate = formatMessageDate(m.internalDate);

          return (
            <article key={m.id} className="message-card">
              <header className="message-card-header">
                <div className="message-sender-profile">
                  <div
                    className="sender-avatar"
                    style={{ backgroundColor: avatarBg }}
                    title={sender.name}
                  >
                    {initial}
                  </div>
                  <div className="sender-details">
                    <div className="message-from-row">
                      <span className="message-from-name">{sender.name}</span>
                      {sender.email && (
                        <span className="message-from-email">&lt;{sender.email}&gt;</span>
                      )}
                    </div>
                    <div className="message-to">
                      to {m.toAddresses?.length > 0 ? m.toAddresses.join(', ') : 'me'}
                    </div>
                  </div>
                </div>

                <div className="message-header-right">
                  <span className="message-date">{formattedDate}</span>
                  <button
                    type="button"
                    className="btn-icon star-btn"
                    title={m.isStarred ? 'Starred' : 'Not starred'}
                    disabled={starringMessageId === m.id}
                    onClick={() => handleToggleStar(m)}
                  >
                    {starringMessageId === m.id ? (
                      <span className="mini-spinner-warn" style={{ width: 14, height: 14 }} />
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill={m.isStarred ? '#f59e0b' : 'none'}
                        stroke={m.isStarred ? '#f59e0b' : '#94a3b8'}
                        strokeWidth="2"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    title="Reply"
                    onClick={() => onCompose('reply', m)}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 17 4 12 9 7" />
                      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                    </svg>
                  </button>
                </div>
              </header>

              <div className="message-body-container">
                <EmailBodyFrame html={m.bodyHtml} text={m.bodyText} />
              </div>

              {m.attachments?.length > 0 && (
                <div className="message-attachments">
                  {m.attachments.map((a) => (
                    <div
                      key={a.id}
                      className="attachment-chip clickable"
                      onClick={() =>
                        downloadOrViewAttachment(a.id, a.filename, false)
                      }
                      title={`Click to open/preview ${a.filename}`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          downloadOrViewAttachment(a.id, a.filename, false);
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      <span className="attachment-name">{a.filename}</span>
                      <span className="attachment-size">
                        ({Math.round(a.sizeBytes / 1024)} KB)
                      </span>
                      <button
                        type="button"
                        className="attachment-download-btn"
                        title={`Download ${a.filename}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadOrViewAttachment(a.id, a.filename, true);
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {lastMessage && (
        <footer className="thread-view-fixed-footer">
          <div className="footer-left-actions">
            {lastChainMessage && (
              <>
            <button
              type="button"
              className="btn btn-subtle"
              onClick={() => onCompose('reply', lastMessage)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 17 4 12 9 7" />
                <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
              </svg>
              <span>Reply</span>
            </button>
            <button
              type="button"
              className="btn btn-subtle"
              onClick={() => onCompose('reply_all', lastMessage)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="7 17 2 12 7 7" />
                <polyline points="12 17 7 12 12 7" />
                <path d="M22 18v-2a4 4 0 0 0-4-4H7" />
              </svg>
              <span>Reply All</span>
            </button>
            <button
              type="button"
              className="btn btn-subtle"
              onClick={() => onCompose('forward', lastMessage)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 17 20 12 15 7" />
                <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
              </svg>
              <span>Forward</span>
            </button>
              </>
            )}
          </div>
          <div className="footer-right-actions">
            <button
              type="button"
              className="btn btn-ghost-danger"
              title="Move to Trash"
              disabled={viewActionLoading === 'trash'}
              onClick={handleTrashThread}
            >
              {viewActionLoading === 'trash' ? (
                <>
                  <span className="mini-spinner-danger" />
                  <span>Trashing…</span>
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span>Trash</span>
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-ghost-warn"
              title="Mark as Spam"
              disabled={viewActionLoading === 'spam'}
              onClick={() => handleSpam(lastMessage.id)}
            >
              {viewActionLoading === 'spam' ? (
                <>
                  <span className="mini-spinner-warn" />
                  <span>Reporting…</span>
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>Spam</span>
                </>
              )}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
